import {view, tokens} from '@sail/ui';
import {useSelector, setValue} from 'src/v2/state/store';
import {Text} from 'src/v2/components/common/Text';
import {AssetWithNetworkIcon} from 'src/v2/components/common/AssetWithNetworkIcon';
import {BrandedButton} from 'src/v2/components/common/BrandedButton';
import {ScreenContentLayout} from 'src/v2/components/screen/ScreenContentLayout';
import {PreferredRefundMethodSection} from 'src/v2/components/anyCrypto/refund/PreferredRefundMethodSection';
import {getNetworkName, getNetworkIcon} from 'src/components/Wallet/constants';
import {getCurrencyIcon} from 'src/v2/util/getCurrencyIcon';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';
import {isValidCryptoAddress} from '@stripe-internal/crypto-utility';
import {useUpdateRefundAddress} from 'src/v2/components/directDeposit/useUpdateRefundAddress';
import {ErrorBanner} from 'src/v2/components/error/ErrorBanner';
import {
  handleError,
  clearErrorBanner,
} from 'src/v2/components/error/handleError';
import type {RefundMethod} from 'src/v2/state/types';
import {useIsAnyCryptoWalletConnectFlow} from 'src/v2/components/anyCrypto/useIsAnyCryptoWalletConnectFlow';

import {message, useIntl} from '@sail/intl';

const SINGLE_NETWORK_TOKENS = new Set(['sol', 'btc']);

const messages = {
  youSelected: message({
    id: 'crypto.payins.v2.refund.you_selected',
    description: 'Label above the selected token in the refund screen',
    defaultMessage: 'You selected',
  }),

  tokenOnNetwork: message<{
    token: React.ReactNode;
    network: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.refund.token_on_network',
    description: 'Selected token and network display',
    defaultMessage: '{token} on {network}',
  }),

  refundExplanation: message<{
    b: (chunks: React.ReactNode) => React.ReactNode;
  }>({
    id: 'crypto.payins.v2.refund.explanation',

    description:
      'Explanation text about when deposits may be refunded and why a refund address is important',

    defaultMessage:
      "In certain cases, your deposit may be refunded. If you're paying from an <b>exchange</b> or <b>a wallet you don't control</b>, set a refund address you own. Without one, returned funds may be permanently lost.",
  }),

  invalidAddress: message<{
    network: string;
  }>({
    id: 'crypto.payins.v2.refund.invalid_address',
    description: 'Error message for invalid crypto address',
    defaultMessage: 'Enter a valid {network} address',
  }),

  continueButton: message({
    id: 'crypto.payins.v2.refund.continue',
    description: 'Continue button on refund screen',
    defaultMessage: 'Continue',
  }),
};

export const RefundScreen = () => {
  const intl = useIntl();
  const [updateRefundAddress, {loading}] = useUpdateRefundAddress();
  const isAnyCryptoWalletConnectFlow = useIsAnyCryptoWalletConnectFlow();

  const selectedToken = useSelector(
    (state) => state.anyCryptoDeposit.selectedToken,
  );
  const supportedCurrencies = useSelector((state) => state.supportedCurrencies);
  const selectedNetwork = useSelector(
    (state) => state.anyCryptoDeposit.selectedNetwork,
  );
  const refundMethod = useSelector(
    (state) => state.anyCryptoDeposit.refundMethod,
  );
  const customRefundAddress = useSelector(
    (state) => state.anyCryptoDeposit.customRefundAddress,
  );

  const handleRefundMethodChange = (id: RefundMethod) => {
    clearErrorBanner();
    setValue((state) => {
      state.anyCryptoDeposit.refundMethod = id;
      state.anyCryptoDeposit.customRefundAddress = '';
    });
  };

  const handleRefundAddressChange = (value: string) => {
    setValue((state) => {
      state.anyCryptoDeposit.customRefundAddress = value;
    });
  };

  const isAddressValid =
    !customRefundAddress.trim().length ||
    !selectedNetwork ||
    isValidCryptoAddress(customRefundAddress.trim(), selectedNetwork);

  const addressError =
    customRefundAddress.trim().length > 0 && selectedNetwork && !isAddressValid
      ? intl.formatMessage(messages.invalidAddress, {
          network: getNetworkName(selectedNetwork),
        })
      : undefined;

  const isButtonDisabled =
    refundMethod === 'setNewRefundAddress' &&
    (customRefundAddress.trim().length === 0 || !!addressError);

  const handleContinue = async () => {
    if (!selectedNetwork) return;

    const refundAddr =
      refundMethod === 'setNewRefundAddress'
        ? customRefundAddress.trim()
        : null;

    clearErrorBanner();
    try {
      await updateRefundAddress({
        refundAddress: refundAddr,
        destinationNetwork: selectedNetwork,
      });
      setValue((state) => {
        state.screen = 'ANY_CRYPTO_DEPOSIT';
      });
    } catch (error) {
      handleError({error, intl, context: 'refund_address'});
    }
  };

  return (
    <ScreenContentLayout
      onBack={() =>
        setValue((state) => {
          const hasMultipleNetworks =
            supportedCurrencies.filter((c) => c.assetCode === selectedToken)
              .length > 1;
          if (hasMultipleNetworks) {
            state.screen = 'ANY_CRYPTO_NETWORK_SELECT';
          } else if (isAnyCryptoWalletConnectFlow) {
            state.screen = 'WALLET_SELECT_GRID';
          } else {
            state.screen = 'TOKEN_SELECT';
          }
        })
      }
      bottom={
        <BrandedButton
          onClick={handleContinue}
          disabled={isButtonDisabled || loading}
          loading={loading}
        >
          {intl.formatMessage(messages.continueButton)}
        </BrandedButton>
      }
    >
      <view.div css={{stack: 'y', gap: 'large'}}>
        {selectedToken && selectedNetwork ? (
          <view.div
            css={{
              stack: 'x',
              gap: 'small',
              alignItems: 'center',
              padding: 'medium',
              borderWidth: 'small',
              borderStyle: 'solid',
              borderColor: tokens.color.neutral[100].toString(),
              borderRadius: 'medium',
            }}
          >
            <AssetWithNetworkIcon
              assetIcon={getCurrencyIcon(selectedToken)}
              networkIcon={
                SINGLE_NETWORK_TOKENS.has(selectedToken)
                  ? undefined
                  : getNetworkIcon(selectedNetwork)
              }
            />
            <view.div css={{stack: 'y'}}>
              <Text
                type="body"
                css={{color: tokens.color.neutral[500].toString()}}
              >
                {intl.formatMessage(messages.youSelected)}
              </Text>
              <Text
                type="heading"
                css={{color: tokens.color.neutral[900].toString()}}
              >
                {intl.formatMessage(messages.tokenOnNetwork, {
                  token: formatAssetCode(selectedToken),
                  network: getNetworkName(selectedNetwork),
                })}
              </Text>
            </view.div>
          </view.div>
        ) : null}

        <Text type="body" css={{color: tokens.color.neutral[900].toString()}}>
          {intl.formatMessage(messages.refundExplanation, {
            b: (chunks: React.ReactNode) => (
              <Text
                type="bodyEmphasized"
                css={{color: tokens.color.neutral[900].toString()}}
              >
                {chunks}
              </Text>
            ),
          })}
        </Text>

        <PreferredRefundMethodSection
          refundMethod={refundMethod}
          onRefundMethodChange={handleRefundMethodChange}
          customRefundAddress={customRefundAddress}
          onRefundAddressChange={handleRefundAddressChange}
          selectedNetwork={selectedNetwork}
          addressError={addressError}
        />
      </view.div>
      <ErrorBanner />
    </ScreenContentLayout>
  );
};
